import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImageUploader({ value, onChange, label, testId = "image-uploader" }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;

    setBusy(true);

    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await api.post("/upload", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Für Cloudinary und lokale Dateien
      onChange(data.url || data.path);

      toast.success(t("admin.uploader.success"));
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId} className="space-y-2">
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={fileUrl(value)} alt="" className="w-20 h-20 object-cover rounded-sm border border-slate-200" />
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid={`${testId}-remove`}
              className="absolute -top-2 -right-2 w-5 h-5 bg-white border border-slate-300 rounded-full grid place-items-center text-slate-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
            >
              <X size={11} />
            </button>
          </div>
        ) : (
          <div className="w-20 h-20 border border-dashed border-slate-300 rounded-sm grid place-items-center text-slate-400">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" />
              <circle cx="9" cy="9" r="2" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testId}-input`}
          />
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-btn`}
            className="rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" /> {t("admin.uploader.uploading")}
              </>
            ) : (
              <>
                <Upload size={14} className="mr-2" /> {label || t("admin.uploader.label")}
              </>
            )}
          </Button>
          <p className="text-xs text-slate-500 mt-1.5">{t("admin.uploader.hint")}</p>
        </div>
      </div>
    </div>
  );
}
